import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Link2, Check, LifeBuoy, BookOpen, ExternalLink, Loader2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { SITE } from "@/lib/site";
import { getHelpArticle, getHelpCatalogue } from "@/lib/api";
import { Container, Kicker, useSeo } from "@/components/ui-kit";
import { CTABand } from "@/components/sections/Social";
import { cn } from "@/lib/utils";

const AUDIENCE = { admin: "Office & admin", client: "Client portal", vendor: "Supplier portal", in_house: "Engineer app" };
let catalogueCache = null;
const loadCatalogue = () => catalogueCache || (catalogueCache = getHelpCatalogue().catch(() => { catalogueCache = null; return null; }));

// Prerendered pages embed the article JSON so the first paint needs no fetch.
const preloaded = (slug) => { const d = window.__HELP_ARTICLE__; return d && d.slug === slug ? d : null; };

const useArticle = (slug) => {
  const [state, setState] = useState(() => ({ doc: preloaded(slug), status: preloaded(slug) ? "ready" : "loading" }));
  useEffect(() => {
    const pre = preloaded(slug);
    if (pre) { setState({ doc: pre, status: "ready" }); return undefined; }
    let on = true;
    setState({ doc: null, status: "loading" });
    getHelpArticle(slug).then((doc) => on && setState({ doc, status: "ready" })).catch((e) => on && setState({ doc: null, status: e?.response?.status === 404 ? "missing" : "error" }));
    return () => { on = false; };
  }, [slug]);
  return state;
};

const useSection = (doc) => {
  const [cat, setCat] = useState(null);
  useEffect(() => { let on = true; loadCatalogue().then((c) => on && setCat(c)); return () => { on = false; }; }, []);
  return useMemo(() => {
    if (!cat || !doc) return [];
    const aud = cat.audiences?.find((a) => a.key === doc.audience);
    return aud?.sections?.find((s) => s.title === doc.section_title || s.key === doc.section)?.articles || [];
  }, [cat, doc]);
};

const Walkthrough = ({ slides }) => {
  const [i, setI] = useState(0);
  if (!slides?.length) return null;
  const s = slides[i];
  return (
    <figure className="rounded-2xl border border-mk-border bg-[#0f0f0c] overflow-hidden" data-testid="help-walkthrough">
      <img src={s.image} alt={s.title} className="block w-full aspect-video object-cover object-top" loading="lazy" />
      <figcaption className="p-4 text-sm text-mk-dim">
        <div className="font-mono text-[10px] uppercase tracking-wider text-mk-lime mb-1">{i + 1} / {slides.length} · {s.title}</div>
        {s.narration}
        {s.audio && <audio controls preload="none" src={s.audio} className="mt-3 w-full h-8" data-testid="help-walkthrough-audio" />}
      </figcaption>
      <div className="flex items-center gap-2 border-t border-mk-border px-3 py-2">
        <button type="button" onClick={() => setI((n) => Math.max(0, n - 1))} disabled={i === 0} className="grid h-8 w-8 place-items-center rounded-full border border-mk-border text-mk-dim hover:text-mk-text disabled:opacity-30" aria-label="Previous step" data-testid="help-walkthrough-prev"><ChevronLeft className="h-4 w-4" /></button>
        <button type="button" onClick={() => setI((n) => Math.min(slides.length - 1, n + 1))} disabled={i === slides.length - 1} className="grid h-8 w-8 place-items-center rounded-full border border-mk-border text-mk-dim hover:text-mk-text disabled:opacity-30" aria-label="Next step" data-testid="help-walkthrough-next"><ChevronRight className="h-4 w-4" /></button>
        <div className="ml-auto flex gap-1">{slides.map((_, n) => <button key={n} type="button" onClick={() => setI(n)} aria-label={`Step ${n + 1}`} className={cn("h-1.5 rounded-full transition-all", n === i ? "w-5 bg-mk-lime" : "w-1.5 bg-mk-border")} />)}</div>
      </div>
    </figure>
  );
};

const Body = ({ html }) => {
  const navigate = useNavigate();
  const onClick = (e) => {
    const a = e.target.closest("a");
    if (!a) return;
    const kb = a.getAttribute("data-kb") || (/^#kb\/([a-z0-9-]+)/.exec(a.getAttribute("href") || "") || [])[1];
    if (kb) { e.preventDefault(); navigate(`/help/${kb}`); }
  };
  // Content is authored by Fixlr's own help team and served from fixlr.app; sanitised anyway (keeps data-kb links).
  const clean = useMemo(() => DOMPurify.sanitize(html || "", { ADD_ATTR: ["data-kb", "target"] }), [html]);
  return <div className="help-article-body" onClick={onClick} dangerouslySetInnerHTML={{ __html: clean }} data-testid="help-article-body" />;
};

const CopyLink = () => {
  const [done, setDone] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(window.location.href); setDone(true); setTimeout(() => setDone(false), 1800); } catch { toast.error("Couldn't copy — use the address bar"); } };
  return <button type="button" onClick={copy} className="btn-ghost !py-2 !px-4 text-xs" data-testid="help-article-copy-link">{done ? <Check className="h-3.5 w-3.5 text-mk-lime" /> : <Link2 className="h-3.5 w-3.5" />}{done ? "Link copied" : "Copy link"}</button>;
};

const Fallback = ({ status }) => (
  <Container className="pt-36 pb-24 text-center" data-testid={`help-article-${status}`}>
    {status === "loading" ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-mk-lime" /> : (
      <>
        <Kicker>Help centre</Kicker>
        <h1 className="mt-4 font-display text-3xl font-bold text-mk-text">{status === "missing" ? "That guide isn't here" : "The help centre is taking a moment"}</h1>
        <p className="mt-3 text-sm text-mk-dim">{status === "missing" ? "It may have moved or been renamed. Search the full help centre instead." : "Try again shortly, or browse the full help centre."}</p>
        <Link to="/help" className="btn-lime mt-6" data-testid="help-article-back">Open the help centre <ArrowRight className="h-4 w-4" /></Link>
      </>
    )}
  </Container>
);

export default function HelpArticle() {
  const { slug } = useParams();
  const { doc, status } = useArticle(slug);
  const siblings = useSection(doc);
  const audience = doc ? AUDIENCE[doc.audience] || "" : "";
  useSeo({ title: doc ? `${doc.title} — Help centre` : "Help centre", description: doc?.summary || "Fixlr help centre guide", path: `/help/${slug}`, image: doc?.thumbnail, type: "article" });
  useEffect(() => {
    if (!doc) return undefined;
    const el = document.createElement("script"); el.type = "application/ld+json"; el.id = "help-article-ld";
    const base = SITE.canonicalUrl;
    el.text = JSON.stringify([
      { "@context": "https://schema.org", "@type": "TechArticle", headline: doc.title, description: doc.summary, image: doc.thumbnail, dateModified: doc.updated_at, url: `${base}/help/${slug}`, author: { "@type": "Organization", name: "Fixlr" }, publisher: { "@type": "Organization", name: "Fixlr", logo: { "@type": "ImageObject", url: `${base}/fixlr-icon.png` } } },
      { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [{ "@type": "ListItem", position: 1, name: "Help centre", item: `${base}/help` }, { "@type": "ListItem", position: 2, name: `${audience} · ${doc.section_title}`, item: `${base}/help` }, { "@type": "ListItem", position: 3, name: doc.title, item: `${base}/help/${slug}` }] },
    ]);
    document.getElementById("help-article-ld")?.remove(); document.head.appendChild(el);
    return () => el.remove();
  }, [doc, slug, audience]);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [slug]);

  if (status !== "ready") return <div data-testid="help-article-page"><Fallback status={status} /></div>;
  return (
    <div data-testid="help-article-page">
      <section className="pt-28 sm:pt-32 pb-8 border-b border-mk-border bg-mk-surface/40">
        <Container>
          <nav className="flex flex-wrap items-center gap-2 text-xs text-mk-muted" aria-label="Breadcrumb" data-testid="help-article-breadcrumb">
            <Link to="/help" className="hover:text-mk-lime inline-flex items-center gap-1"><ArrowLeft className="h-3 w-3" />Help centre</Link><span>/</span><span>{audience}</span><span>/</span><span className="text-mk-dim">{doc.section_title}</span>
          </nav>
          <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-mk-text text-balance max-w-3xl" data-testid="help-article-title">{doc.title}</h1>
          {doc.summary && <p className="mt-4 text-base sm:text-lg text-mk-dim max-w-2xl leading-relaxed" data-testid="help-article-summary">{doc.summary}</p>}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <CopyLink />
            <a href={SITE.loginUrl} target="_blank" rel="noreferrer" className="btn-ghost !py-2 !px-4 text-xs" data-testid="help-article-open-app">Open Fixlr <ExternalLink className="h-3.5 w-3.5" /></a>
            {doc.updated_at && <span className="ml-auto font-mono text-[11px] text-mk-muted" data-testid="help-article-updated">Updated {new Date(doc.updated_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>}
          </div>
        </Container>
      </section>

      <Container className="py-12 grid lg:grid-cols-[minmax(0,1fr)_300px] gap-12 items-start">
        <article className="min-w-0 max-w-3xl">
          <Walkthrough slides={doc.walkthrough?.slides} />
          <div className={cn(doc.walkthrough?.slides?.length && "mt-8")}><Body html={doc.content_html} /></div>
          {doc.related?.length > 0 && (
            <div className="mt-12 border-t border-mk-border pt-6" data-testid="help-article-related">
              <div className="eyebrow">Related guides</div>
              <div className="mt-3 flex flex-wrap gap-2">{doc.related.map((r) => <Link key={r.slug} to={`/help/${r.slug}`} className="rounded-full border border-mk-border px-3 py-1.5 text-xs text-mk-dim hover:border-mk-lime hover:text-mk-lime transition-colors" data-testid={`help-related-${r.slug}`}>{r.title}</Link>)}</div>
            </div>
          )}
        </article>
        <aside className="lg:sticky lg:top-24 space-y-4">
          {siblings.length > 1 && (
            <div className="rounded-2xl border border-mk-border bg-mk-surface p-5" data-testid="help-article-section-nav">
              <div className="eyebrow inline-flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" />{doc.section_title}</div>
              <ul className="mt-3 space-y-1.5">{siblings.map((a) => <li key={a.slug}><Link to={`/help/${a.slug}`} className={cn("block text-[13px] leading-snug transition-colors", a.slug === slug ? "text-mk-lime" : "text-mk-dim hover:text-mk-text")} aria-current={a.slug === slug ? "page" : undefined} data-testid={`help-section-link-${a.slug}`}>{a.title}</Link></li>)}</ul>
            </div>
          )}
          <div className="rounded-2xl border border-mk-lime/30 bg-mk-lime/[0.04] p-5">
            <div className="eyebrow inline-flex items-center gap-1.5"><LifeBuoy className="h-3.5 w-3.5" />Still stuck?</div>
            <p className="mt-2 text-sm text-mk-dim">Ask the assistant in the corner — it answers from these guides — or send us a note.</p>
            <div className="mt-4 flex flex-col gap-2">
              <Link to="/help" className="btn-ghost !py-2 text-xs justify-center" data-testid="help-article-all-guides"><MessageSquare className="h-3.5 w-3.5" />Search all guides</Link>
              <Link to="/contact" className="btn-lime !py-2 text-xs justify-center" data-testid="help-article-contact">Contact support</Link>
            </div>
          </div>
        </aside>
      </Container>
      <CTABand />
    </div>
  );
}
